import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, getApiBaseUrl } from '../api/client';
import './AdminPanel.css';

const DEFAULT_STORE_HOURS = [
  { day: 'Monday', open: '07:30', close: '17:00', closed: false },
  { day: 'Tuesday', open: '07:30', close: '17:00', closed: false },
  { day: 'Wednesday', open: '07:30', close: '17:00', closed: false },
  { day: 'Thursday', open: '07:30', close: '17:00', closed: false },
  { day: 'Friday', open: '07:30', close: '17:00', closed: false },
  { day: 'Saturday', open: '', close: '', closed: true },
  { day: 'Sunday', open: '', close: '', closed: true },
  { day: 'Stat Holidays', open: '', close: '', closed: true },
];

const NAV_ITEMS = [
  { id: 'dashboard', icon: 'fa-tachometer',   label: 'Dashboard' },
  { id: 'gallery',   icon: 'fa-image',        label: 'Gallery' },
  { id: 'settings',  icon: 'fa-cog',          label: 'Settings' },
];

/* ── Animated Counter ─────────────────────── */
function AnimCounter({ target }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const duration = 1200;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target]);
  return <>{count.toLocaleString()}</>;
}

/* ── Sections ─────────────────────────────── */
function Dashboard({ photos, site }) {
  const stats = [
    { id: 'stat-gallery', icon: 'fa-image', label: 'Gallery Photos', value: photos.length, color: '#f59e0b', suffix: '' },
  ];
  return (
    <div className="ap-section">
      <h2 className="ap-section-title">Dashboard Overview</h2>

      {/* Stats Grid */}
      <div className="ap-stats-grid">
        {stats.map((s) => (
          <div className="ap-stat-card" key={s.id} id={s.id}>
            <div className="ap-stat-icon" style={{ background: `${s.color}22`, color: s.color }}>
              <i className={`fa ${s.icon}`} />
            </div>
            <div className="ap-stat-info">
              <span className="ap-stat-value" style={{ color: s.color }}>
                <AnimCounter target={s.value} />{s.suffix}
              </span>
              <span className="ap-stat-label">{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Info */}
      <div className="ap-quick-grid">
        <div className="ap-quick-card">
          <div className="ap-quick-header">
            <h3><i className="fa fa-clock-o" /> Recent Activity</h3>
          </div>
          <ul className="ap-activity-list">
            <li><span className="ap-dot ap-dot--blue" />{photos.length} gallery photo(s) available <span>now</span></li>
          </ul>
        </div>
        <div className="ap-quick-card">
          <div className="ap-quick-header">
            <h3><i className="fa fa-info-circle" /> Pharmacy Info</h3>
          </div>
          <div className="ap-info-list">
            <div className="ap-info-row">
              <span className="ap-info-label"><i className="fa fa-map-marker" /> Address</span>
              <span>{site.address}</span>
            </div>
            <div className="ap-info-row">
              <span className="ap-info-label"><i className="fa fa-phone" /> Phone</span>
              <span>{site.phone}</span>
            </div>
            <div className="ap-info-row">
              <span className="ap-info-label"><i className="fa fa-envelope" /> Email</span>
              <span>{site.email}</span>
            </div>
            <div className="ap-info-row">
              <span className="ap-info-label"><i className="fa fa-globe" /> Website</span>
              <span>revivalpharmacy.ca</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Gallery({ photos, setPhotos, onError }) {
  const [uploading, setUploading] = useState(false);
  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter((file) => file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024);
    if (validFiles.length !== files.length) onError('Only image files up to 5MB are allowed.');
    setUploading(true);
    try {
      const uploaded = await Promise.all(validFiles.map((file) => api.uploadGallery(file)));
      setPhotos((current) => [...uploaded.map((result) => result.item), ...current]);
    } catch (error) {
      onError(error.message);
    } finally {
      setUploading(false);
    }
    event.target.value = '';
  };

  const removePhoto = async (id) => {
    try {
      await api.deleteGallery(id);
      setPhotos((current) => current.filter((photo) => photo._id !== id));
    } catch (error) {
      onError(error.message);
    }
  };

  return (
    <div className="ap-section">
      <h2 className="ap-section-title">Gallery Management</h2>
      <div className="ap-upload-area" id="gallery-upload-zone">
        <i className="fa fa-cloud-upload" />
        <p>Drag & drop images here, or <label htmlFor="gallery-file-input" className="ap-upload-link">Browse Files</label></p>
        <input id="gallery-file-input" type="file" accept="image/*" multiple hidden onChange={handleFiles} />
        <span>{uploading ? 'Uploading...' : 'PNG, JPG, WEBP — max 5MB each'}</span>
      </div>
      <div className="ap-gallery-grid">
        {photos.map((p) => (
          <div className="ap-gallery-item" key={p._id}>
            <div className="ap-gallery-thumb">
              <img
                src={`${getApiBaseUrl().replace('/api', '')}${p.url}`}
                alt={p.name}
                onError={(e) => { e.target.src = '/assets/image/r1.jpeg'; }}
              />
            </div>
            <div className="ap-gallery-overlay">
              <button className="ap-btn ap-btn--sm ap-btn--red" onClick={() => removePhoto(p._id)}>
                <i className="fa fa-trash" /> Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Settings({ admin, logout, site, setSite, onError }) {
  const [siteForm, setSiteForm] = useState({
    ...site,
    storeHours: site.storeHours?.length ? site.storeHours : DEFAULT_STORE_HOURS,
  });
  useEffect(() => {
    setSiteForm({
      ...site,
      alternatePhone: site.alternatePhone || '',
      storeHours: site.storeHours?.length ? site.storeHours : DEFAULT_STORE_HOURS,
    });
  }, [site]);

  const saveSite = async () => {
    const cleaned = {
      ...siteForm,
      name: siteForm.name.trim(),
      phone: siteForm.phone.trim(),
      alternatePhone: siteForm.alternatePhone.trim(),
      email: siteForm.email.trim(),
      address: siteForm.address.trim(),
      storeHours: siteForm.storeHours,
    };
    if (!cleaned.name || !cleaned.email) {
      onError('Pharmacy name and email are required.');
      return;
    }
    try {
      const saved = await api.updateSettings(cleaned);
      setSite(saved);
    } catch (error) {
      onError(error.message);
    }
  };

  return (
    <div className="ap-section">
      <h2 className="ap-section-title">Settings</h2>
      <div className="ap-settings-grid">
        <div className="ap-settings-card">
          <h3><i className="fa fa-user-circle" /> Admin Profile</h3>
          <div className="ap-settings-avatar">
            <div className="ap-avatar-circle">
              <i className="fa fa-user" />
            </div>
            <div>
              <strong>{admin?.name || 'Administrator'}</strong>
              <span>{admin?.role || 'Admin'}</span>
            </div>
          </div>
          <div className="ap-settings-form">
            <div className="ap-settings-field">
              <label>Username</label>
              <input type="text" defaultValue={admin?.username || 'admin'} className="ap-settings-input" readOnly />
            </div>
          </div>
        </div>

        <div className="ap-settings-card">
          <h3><i className="fa fa-cog" /> Site Settings</h3>
          <div className="ap-settings-form">
            <div className="ap-settings-field">
              <label>Pharmacy Name</label>
              <input type="text" value={siteForm.name} onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })} className="ap-settings-input" />
            </div>
            <div className="ap-settings-field">
              <label>Phone Number</label>
              <input type="text" value={siteForm.phone} onChange={(e) => setSiteForm({ ...siteForm, phone: e.target.value })} className="ap-settings-input" />
            </div>
            <div className="ap-settings-field">
              <label>Alternate Phone Number</label>
              <input type="text" value={siteForm.alternatePhone} onChange={(e) => setSiteForm({ ...siteForm, alternatePhone: e.target.value })} className="ap-settings-input" placeholder="Optional alternate number" />
            </div>
            <div className="ap-settings-field">
              <label>Email Address</label>
              <input type="email" value={siteForm.email} onChange={(e) => setSiteForm({ ...siteForm, email: e.target.value })} className="ap-settings-input" />
            </div>
            <div className="ap-settings-field">
              <label>Address</label>
              <input type="text" value={siteForm.address} onChange={(e) => setSiteForm({ ...siteForm, address: e.target.value })} className="ap-settings-input" />
            </div>
            <button className="ap-btn ap-btn--green" onClick={saveSite}>
              <i className="fa fa-save" /> Save Settings
            </button>
          </div>
        </div>

        <div className="ap-settings-card">
          <h3><i className="fa fa-clock-o" /> Store Hours</h3>
          <div className="ap-settings-form">
            {siteForm.storeHours.map((hour, index) => (
              <div className="ap-settings-field ap-hours-field" key={hour.day}>
                <label>{hour.day}</label>
                <div className="ap-hours-controls">
                  <input
                    type="time"
                    value={hour.open}
                    disabled={hour.closed}
                    onChange={(event) => {
                      const storeHours = siteForm.storeHours.map((item, itemIndex) => (
                        itemIndex === index ? { ...item, open: event.target.value } : item
                      ));
                      setSiteForm({ ...siteForm, storeHours });
                    }}
                    className="ap-settings-input"
                  />
                  <span>to</span>
                  <input
                    type="time"
                    value={hour.close}
                    disabled={hour.closed}
                    onChange={(event) => {
                      const storeHours = siteForm.storeHours.map((item, itemIndex) => (
                        itemIndex === index ? { ...item, close: event.target.value } : item
                      ));
                      setSiteForm({ ...siteForm, storeHours });
                    }}
                    className="ap-settings-input"
                  />
                  <label className="ap-hours-closed">
                    <input
                      type="checkbox"
                      checked={hour.closed}
                      onChange={(event) => {
                        const storeHours = siteForm.storeHours.map((item, itemIndex) => (
                          itemIndex === index
                            ? { ...item, closed: event.target.checked }
                            : item
                        ));
                        setSiteForm({ ...siteForm, storeHours });
                      }}
                    />
                    Closed
                  </label>
                </div>
              </div>
            ))}
            <button className="ap-btn ap-btn--green" onClick={saveSite}>
              <i className="fa fa-save" /> Save Store Hours
            </button>
          </div>
        </div>

        <div className="ap-settings-card ap-settings-card--danger">
          <h3><i className="fa fa-sign-out" /> Session</h3>
          <p>You are currently logged in as <strong>{admin?.username || 'admin'}</strong>.</p>
          <button className="ap-btn ap-btn--red" onClick={logout} id="admin-logout-btn">
            <i className="fa fa-sign-out" /> Logout from Admin Panel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main AdminPanel Component ─────────────── */
export default function AdminPanel() {
  const { isAuthenticated, admin, logout } = useAuth();
  const [activeNav, setActiveNav] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [site, setSite] = useState({
    name: '',
    phone: '',
    alternatePhone: '',
    email: '',
    address: '',
    storeHours: DEFAULT_STORE_HOURS,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      window.location.href = '/admin/login';
      return;
    }
    Promise.all([api.gallery(), api.settings()])
      .then(([galleryResult, settings]) => {
        setPhotos(galleryResult.items);
        setSite(settings);
      })
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  const handleLogout = () => {
    logout();
    window.location.href = '/admin/login';
  };

  const renderSection = () => {
    switch (activeNav) {
      case 'dashboard': return <Dashboard photos={photos} site={site} />;
      case 'gallery':   return <Gallery photos={photos} setPhotos={setPhotos} onError={setError} />;
      case 'settings':  return <Settings admin={admin} logout={handleLogout} site={site} setSite={setSite} onError={setError} />;
      default:          return <Dashboard />;
    }
  };

  if (!isAuthenticated) return null;
  if (loading) return <div className="ap-root"><main className="ap-content">Loading admin data...</main></div>;

  return (
    <div className={`ap-root${sidebarOpen ? ' ap-sidebar-open' : ''}`}>
      {/* ── Sidebar ── */}
      <aside className="ap-sidebar" id="admin-sidebar">
        <div className="ap-sidebar-logo">
          <img src="/assets/image/logo.png" alt="Revival Pharmacy" />
          <span>Admin</span>
        </div>

        <nav className="ap-sidebar-nav">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              id={`nav-${item.id}`}
              className={`ap-nav-item${activeNav === item.id ? ' ap-nav-item--active' : ''}`}
              onClick={() => { setActiveNav(item.id); setSidebarOpen(false); }}
            >
              <i className={`fa ${item.icon}`} />
              <span>{item.label}</span>
              {item.badge && <span className="ap-nav-badge">{item.badge}</span>}
            </button>
          ))}
        </nav>

        <div className="ap-sidebar-footer">
          <div className="ap-user-mini">
            <div className="ap-user-avatar"><i className="fa fa-user" /></div>
            <div className="ap-user-info">
              <strong>{admin?.username || 'admin'}</strong>
              <span>{admin?.role || 'Administrator'}</span>
            </div>
          </div>
          <button className="ap-logout-mini" onClick={handleLogout} title="Logout">
            <i className="fa fa-sign-out" />
          </button>
        </div>
      </aside>

      {/* Sidebar overlay for mobile */}
      {sidebarOpen && (
        <div className="ap-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Main Content ── */}
      <div className="ap-main">
        {/* Top Bar */}
        <header className="ap-topbar">
          <button
            className="ap-menu-toggle"
            onClick={() => setSidebarOpen((o) => !o)}
            aria-label="Toggle sidebar"
          >
            <i className="fa fa-bars" />
          </button>
          <div className="ap-topbar-title">
            {NAV_ITEMS.find((n) => n.id === activeNav)?.label || 'Dashboard'}
          </div>
          <div className="ap-topbar-right">
            <a href="/" target="_blank" rel="noopener noreferrer" className="ap-topbar-btn" title="View Website">
              <i className="fa fa-external-link" /> View Site
            </a>
            <button className="ap-topbar-btn ap-topbar-btn--logout" onClick={handleLogout}>
              <i className="fa fa-sign-out" /> Logout
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="ap-content">
          {error && <div className="ap-error-banner" role="alert">{error}</div>}
          {renderSection()}
        </main>
      </div>
    </div>
  );
}
